import { useMemo, useState } from 'react';
import { useAsyncService } from '../../../../expressions/repository/async/useAsyncService';
import { useAsyncServiceGetData } from '../../../../expressions/repository/async/useAsyncServiceGetData';
import { useWidgetScopeContext } from '../../../../form/fillable/WidgetScopeContext';
import { useWidgetVisibility } from '../../../hooks/useWidgetVisibility';
import { IWidgetComponentProps } from '../../../IWidgetType';
import { getWidgetProps } from '../common/util/getWidgetProps';
import { AsyncSelectWidgetSelectInput } from './components/select/AsyncSelectWidgetSelectInput';
import { AsyncServiceTableInput } from './components/table/AsyncServiceTableInput';
import { asyncSelectWidgetObjectName } from './hooks/asyncSelectObjectName';
import { IAsyncSelectWidget } from './IAsyncSelectWidget';

export interface IAsyncSelectWidgetComponentProps
  extends IWidgetComponentProps<IAsyncSelectWidget> {}

export function AsyncSelectWidgetComponent(
  props: IAsyncSelectWidgetComponentProps,
) {
  const { widget } = props;

  const widgetOptions = getWidgetProps(props);

  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const { prefix } = useWidgetScopeContext();

  const { service, axiosClient } = useAsyncService(widget.serviceId);

  // Se configurato il "filtro di ricerca", viene usato direttamente
  // come parametro di ricerca del servizio
  const searchParam = useMemo(() => {
    return widget.searchField && searchQuery
      ? { [widget.searchField]: searchQuery }
      : undefined;
  }, [widget.searchField, searchQuery]);

  const visible = useWidgetVisibility(props.widget, widgetOptions);

  const objectFieldName = useMemo(
    () => `${prefix}${asyncSelectWidgetObjectName(widget.code)}`,
    [prefix, widget.code],
  );

  const { data, error, loading } = useAsyncServiceGetData({
    basePath: prefix,
    client: axiosClient,
    service,
    inputs: widget.inputs,
    searchParam,
    disabled: widgetOptions.readonly || !visible,
  });

  if (widget.renderType === 'table') {
    return (
      <AsyncServiceTableInput
        widgetOptions={widgetOptions}
        data={data}
        isLoading={loading}
        error={error}
        widget={widget}
        setSearchQuery={setSearchQuery}
        objectFieldName={objectFieldName}
        service={service}
      />
    );
  }

  return (
    <AsyncSelectWidgetSelectInput
      widget={widget}
      widgetOptions={widgetOptions}
      readonly={props.readonly}
      data={data}
      isLoading={loading}
      error={error}
      objectFieldName={objectFieldName}
      service={service}
      setSearchQuery={setSearchQuery}
      searchQuery={searchQuery}
      name={props.name}
    />
  );
}
